import { useContext } from 'react';
import { Handle, Position } from '@xyflow/react';
import { useIntl } from 'react-intl';
import { Link as RouterLink } from 'react-router-dom';
import { IconExternalLink } from '@rossum/ui/icons/tabler';
import {
  Box,
  IconButton,
  Stack,
  SvgIcon,
  Tooltip,
  Typography,
  useTheme,
} from '@rossum/ui/material';
import { borderRadius } from '@rossum/ui/theme';
import { NODE_WIDTH } from './computeLayout';
import { GraphContext } from './GraphContext';
import { NodeDetailSection } from './NodeDetailSection';

type DetailSection = { label: string; items: string[] };

type BaseNodeProps = {
  id: string;
  label: string;
  accentColor: string;
  accentLightColor: string;
  icon: React.ReactNode;
  dashed?: boolean;
  baseOpacity?: number;
  minHeight: number;
  tooltipTitle: string;
  tooltipEnterDelay?: number;
  detailSections: DetailSection[];
  warningMessage?: string;
  /** The node's value is computed (formula/reasoning/lookup) — a cycle then
   * means it may be evaluated from stale data, and the warning says so. */
  hasComputedValue?: boolean;
};

export const BaseNode = ({
  id,
  label,
  accentColor,
  accentLightColor,
  icon,
  dashed = false,
  baseOpacity = 1,
  minHeight,
  tooltipTitle,
  tooltipEnterDelay = 500,
  detailSections,
  warningMessage,
  hasComputedValue = false,
}: BaseNodeProps) => {
  const theme = useTheme();
  const intl = useIntl();
  const { selectedNodeId, connectedIds, cycleNodeIds, getNodeUrl } =
    useContext(GraphContext);
  const nodeUrl = getNodeUrl(id);

  const isActive = selectedNodeId === id;
  const isConnected = connectedIds.has(id);
  const isDimmed = selectedNodeId !== null && !isConnected;
  const isInCycle = cycleNodeIds.has(id);

  const cycleWarning = intl.formatMessage({
    id: hasComputedValue
      ? 'containers.settings.queues.queue.overview.cycles.nodeWarningComputed'
      : 'containers.settings.queues.queue.overview.cycles.nodeWarning',
  });
  const warnings = [
    ...(warningMessage
      ? [{ text: warningMessage, color: 'warning.main' }]
      : []),
    ...(isInCycle ? [{ text: cycleWarning, color: 'error.main' }] : []),
  ];

  const hasSections = detailSections.some(s => s.items.length > 0);
  const hasDetails = hasSections || warnings.length > 0;

  // A node on a circular dependency keeps its error border in every state —
  // the problem marker must not disappear on selection.
  const borderColor = isInCycle
    ? theme.palette.error.main
    : isActive || dashed
      ? accentColor
      : isConnected && selectedNodeId
        ? accentLightColor
        : theme.palette.divider;

  return (
    <>
      {/* eslint-disable-next-line no-restricted-syntax -- ReactFlow Handle does not support sx */}
      <Handle type="target" position={Position.Left} style={{ opacity: 0 }} />
      <Box
        sx={{
          border: '1px',
          borderStyle: dashed ? 'dashed' : 'solid',
          borderColor,
          borderRadius: borderRadius('m'),
          px: 1,
          py: 0.5,
          backgroundColor: theme.palette.background.paper,
          width: NODE_WIDTH,
          minHeight,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: isActive && hasDetails ? 'flex-start' : 'center',
          cursor: 'pointer',
          opacity: isDimmed ? 0.35 : baseOpacity,
          transition: 'opacity 0.15s, border-color 0.15s',
          zIndex: isActive ? 10 : 0,
          '&:hover': {
            borderColor: isInCycle
              ? theme.palette.error.light
              : accentLightColor,
          },
        }}
      >
        <Stack direction="row" alignItems="center" spacing={0.75}>
          {icon}
          <Tooltip
            title={isActive ? '' : tooltipTitle}
            enterDelay={tooltipEnterDelay}
          >
            <Typography variant="body2" noWrap sx={{ flex: 1, minWidth: 0 }}>
              {label}
            </Typography>
          </Tooltip>
          {nodeUrl && (
            <IconButton
              component={RouterLink}
              to={nodeUrl}
              target="_blank"
              size="small"
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
            >
              <SvgIcon fontSize="small">
                <IconExternalLink />
              </SvgIcon>
            </IconButton>
          )}
        </Stack>
        {isActive && hasDetails && (
          <Box
            sx={{
              borderTop: `1px solid ${theme.palette.divider}`,
              mt: 1,
              pt: 1,
            }}
          >
            {warnings.map(warning => (
              <Typography
                key={warning.text}
                variant="caption"
                sx={{
                  color: warning.color,
                  display: 'block',
                  mb: hasSections ? 0.5 : 0,
                }}
              >
                {warning.text}
              </Typography>
            ))}
            {detailSections.map(section => (
              <NodeDetailSection
                key={section.label}
                label={section.label}
                items={section.items}
              />
            ))}
          </Box>
        )}
      </Box>
      {/* eslint-disable-next-line no-restricted-syntax -- ReactFlow Handle does not support sx */}
      <Handle type="source" position={Position.Right} style={{ opacity: 0 }} />
    </>
  );
};
